import { useState } from 'react';

import { requestApi } from 'api/Api';

import { showAlert } from 'components/shared/Alert/Alert';
import {
  CHUNK_SIZE,
  FILE_UPLOAD_STATUS
} from 'components/admin/pages/GeneralUploader/components/lib/constants';

// For a folder upload the browser gives webkitRelativePath as
// "<pickedFolder>/<sub>/<file>". Strip the picked root so its CONTENTS map to
// the batch root (data/<vendor>/<batch>/<sub>/<file>), mirroring how a zip of
// the export root extracts. A single (non-folder) file just returns its name.
// It also doubles as the progress-row key: two files can share a name across
// sub-folders, but not a relative path.
export const toRelativePath = (file) => {
  const p = file.webkitRelativePath || file.name;
  const idx = p.indexOf('/');
  return idx >= 0 ? p.slice(idx + 1) : p;
};

const useFileUploader = () => {
  const [uploading, setUploading] = useState(false);

  const updateFileStatus = (fileKey, status, setUploadProgressList) => {
    setUploadProgressList((prev) =>
      prev.map((p) => (p.fileKey === fileKey ? { ...p, status } : p))
    );
  };

  const updateFileProgress = (fileKey, progress, status, setUploadProgressList) => {
    setUploadProgressList((prev) =>
      prev.map((p) => (p.fileKey === fileKey ? { ...p, progress, status } : p))
    );
  };

  // Upload one file via S3 multipart. Returns the object's key only if every part
  // uploaded AND complete-upload succeeded. On any failure it marks the file ERROR
  // and returns null WITHOUT calling complete-upload — completing a truncated part
  // set would assemble a corrupt object the importer then processes as valid.
  const uploadOne = async (file, selectedPractice, selectedVendor, importInfoId, setList) => {
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);
    const fileKey = toRelativePath(file);

    const { info } = await requestApi({
      url: '/api/admin/uploader/initiate-multipart',
      params: {
        filename: file.name,
        // Folder-relative path (picked root stripped); the backend routes zips to
        // zip/ and everything else to data/<vendor>/<batch>/<relativePath>.
        relativePath: fileKey,
        contentType: file.type,
        practiceId: selectedPractice.id,
        vendorId: selectedVendor.id,
        importInfoId
      },
      onError: (error) => {
        showAlert({ title: error ?? `Couldn't upload ${file.name}`, color: 'danger' });
      }
    });
    // requestApi never rejects — a network failure or code -1 returns without ever
    // reaching onError, so the row has to be marked here or it hangs at INITIATED.
    if (!info) {
      updateFileStatus(fileKey, FILE_UPLOAD_STATUS.ERROR, setList);
      return null;
    }

    const { uploadId, key } = info;
    const parts = [];

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);

      const { signedUrl } = await requestApi({
        url: '/api/admin/uploader/presign-part',
        params: { key, uploadId, partNumber, importInfoId },
        onError: (error) => {
          showAlert({ title: error ?? `Couldn't upload ${file.name}`, color: 'danger' });
        }
      });
      if (!signedUrl) {
        updateFileStatus(fileKey, FILE_UPLOAD_STATUS.ERROR, setList);
        return null;
      }

      // The presigned PUT must go out as a plain fetch: our axios instance sends
      // withCredentials (S3 rejects credentialed CORS) and requestApi treats S3's
      // empty body as an unknown code, notifying Honeybadger on every good part.
      let etag = null;
      try {
        const response = await fetch(signedUrl, { method: 'PUT', body: blob });
        if (!response.ok) {
          updateFileStatus(fileKey, FILE_UPLOAD_STATUS.ERROR, setList);
          showAlert({
            title: `Couldn't upload ${file.name} (part ${partNumber} failed)`,
            color: 'danger'
          });
          return null;
        }
        etag = response.headers.get('ETag')?.replace(/"/g, '');
      } catch (err) {
        updateFileStatus(fileKey, FILE_UPLOAD_STATUS.ERROR, setList);
        showAlert({ title: err?.message || `Couldn't upload ${file.name}`, color: 'danger' });
        return null;
      }

      if (!etag) {
        // S3 CORS must expose the ETag header; without it we can't complete the
        // multipart upload. Fail this file rather than throwing (which would
        // leave `uploading` stuck true and wedge the whole batch).
        updateFileStatus(fileKey, FILE_UPLOAD_STATUS.ERROR, setList);
        showAlert({
          title: `Upload failed for ${file.name}: S3 did not expose the ETag (check bucket CORS ExposeHeaders)`,
          color: 'danger'
        });
        return null;
      }

      parts.push({ ETag: etag, PartNumber: partNumber });
      updateFileProgress(fileKey, partNumber, FILE_UPLOAD_STATUS.UPLOADING, setList);
    }

    // Every part uploaded — safe to finalize.
    let completed = false;
    await requestApi({
      url: '/api/admin/uploader/complete-upload',
      params: { key, uploadId, parts, importInfoId, filename: file.name, fileType: file.type },
      onSuccess: () => {
        completed = true;
        updateFileStatus(fileKey, FILE_UPLOAD_STATUS.UPLOADED, setList);
        showAlert({ title: `${file.name} uploaded`, color: 'success' });
      },
      onError: (error) => {
        showAlert({ title: error ?? `Couldn't upload ${file.name}`, color: 'danger' });
      }
    });
    if (!completed) {
      updateFileStatus(fileKey, FILE_UPLOAD_STATUS.ERROR, setList);
      return null;
    }
    return key;
  };

  // `importRequestId` ties the batch to an import request: the backend records it
  // on import_info and then HOLDS the batch at finalize instead of launching, so
  // the operator decides when (and how often) it runs.
  const uploadFile = async (
    files,
    selectedPractice,
    selectedVendor,
    setUploadProgressList,
    { importRequestId } = {}
  ) => {
    if (!files || files.length === 0)
      return {
        importInfoId: null,
        success: false,
        error: 'No files were selected.',
        files: []
      };
    setUploading(true);

    let importInfoId = null;
    let finalized = false;
    // Every file that fully landed, with its S3 key — a caller that wants to read
    // one straight back (a CSV to column-map) needs the key, not just a yes/no.
    const uploaded = [];

    try {
      const response = await requestApi({
        url: '/api/admin/uploader/initiate-upload',
        params: {
          practiceId: selectedPractice.id,
          vendorId: selectedVendor.id,
          importRequestId
        },
        onError: (error) => {
          showAlert({ title: error ?? `Couldn't start the upload`, color: 'danger' });
        }
      });

      if (!response?.importInfoId)
        return {
          importInfoId: null,
          success: false,
          error: `Couldn't start the upload`,
          files: []
        };
      importInfoId = response.importInfoId;

      for (const file of files) {
        try {
          const key = await uploadOne(
            file,
            selectedPractice,
            selectedVendor,
            importInfoId,
            setUploadProgressList
          );
          if (key) uploaded.push({ key, name: file.name, size: file.size });
        } catch (err) {
          // A single bad file never aborts the batch (important for folders).
          updateFileStatus(toRelativePath(file), FILE_UPLOAD_STATUS.ERROR, setUploadProgressList);
          showAlert({ title: err?.message || `Couldn't upload ${file.name}`, color: 'danger' });
        }
      }

      // Trigger processing ONCE, after every file: the backend decides unzip
      // (any zips present) vs import-directly (only data files). A batch with a
      // failed file is never finalized — the importer would run on a partial
      // export and report it as a valid result.
      if (uploaded.length && uploaded.length === files.length) {
        await requestApi({
          url: '/api/admin/uploader/finalize-upload',
          params: { importInfoId },
          onSuccess: () => {
            finalized = true;
          },
          onError: (error) => {
            showAlert({
              title: error ?? `Files uploaded, but processing couldn't be started`,
              color: 'danger'
            });
          }
        });
      }
    } finally {
      setUploading(false);
    }

    // Only advance the caller (to the processing/overview view) when every file
    // landed AND processing actually started — a held-back finalize leaves the
    // batch sitting in S3 with nothing to watch.
    const uploadedAll = uploaded.length === files.length;
    const success = uploadedAll && finalized;

    let error = null;
    if (!uploaded.length) error = 'Nothing was uploaded.';
    else if (!uploadedAll)
      error = `Only ${uploaded.length} of ${files.length} file(s) uploaded — nothing was queued for import.`;
    else if (!finalized) error = `Files uploaded, but processing couldn't be started.`;

    return { importInfoId, success, error, files: uploaded };
  };

  return { uploading, uploadFile };
};

export default useFileUploader;
